import classnames from 'classnames'
import Dialog, { DialogProps as IDialogPropTypes } from 'rc-dialog'
import { context } from 'rc-image/es/PreviewGroup'
import getFixScaleEleTransPosition from 'rc-image/lib/getFixScaleEleTransPosition'
import useFrameSetState from 'rc-image/lib/hooks/useFrameSetState'
// @ts-ignore
import addEventListener from 'rc-util/es/Dom/addEventListener'
import { warning } from 'rc-util/lib/warning'
import React from 'react'

export interface PreviewProps extends Omit<IDialogPropTypes, 'onClose'> {
  onClose?: (e: React.SyntheticEvent<Element>) => void
  src?: string
  alt?: string
  icons?: {
    rotateLeft?: React.ReactNode
    rotateRight?: React.ReactNode
    zoomIn?: React.ReactNode
    zoomOut?: React.ReactNode
    close?: React.ReactNode
    left?: React.ReactNode
    right?: React.ReactNode
  }
}

const initialPosition = {
  x: 0,
  y: 0,
}

export const ImagePreview: React.FC<PreviewProps> = (props) => {
  const { prefixCls, src, alt, onClose, visible, icons = {}, ...restProps } = props
  const { rotateLeft, rotateRight, zoomIn, zoomOut, close, left, right } = icons
  const [scale, setScale] = React.useState(1)
  const [rotate, setRotate] = React.useState(0)
  const [position, setPosition] = useFrameSetState<{
    x: number
    y: number
  }>(initialPosition)
  const imgRef = React.useRef<HTMLImageElement>()
  const originPositionRef = React.useRef<{
    originX: number
    originY: number
    deltaX: number
    deltaY: number
  }>({
    originX: 0,
    originY: 0,
    deltaX: 0,
    deltaY: 0,
  })
  const [isMoving, setMoving] = React.useState(false)
  const [imageWidth, setImageWidth] = React.useState('0%')
  const { previewUrls, current, isPreviewGroup, setCurrent } = React.useContext(context)
  const previewGroupCount = previewUrls.size
  const previewUrlsKeys = Array.from(previewUrls.keys())
  const currentPreviewIndex = previewUrlsKeys.indexOf(current)
  const combinationSrc = isPreviewGroup ? previewUrls.get(current) : src
  const showLeftOrRightSwitches = isPreviewGroup && previewGroupCount > 1

  const onAfterClose = () => {
    setScale(1)
    setRotate(0)
    setPosition(initialPosition)
  }

  const onZoomIn = () => {
    setScale((value) => value + 1)
    setPosition(initialPosition)
  }

  const onZoomOut = () => {
    if (scale > 1) {
      setScale((value) => value - 1)
    }
    setPosition(initialPosition)
  }

  const onRotateRight = () => {
    setRotate((value) => value + 90)
  }

  const onRotateLeft = () => {
    setRotate((value) => value - 90)
  }

  const onSwitchLeft: React.MouseEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault()
    // Without this mask close will abnormal
    event.stopPropagation()
    if (currentPreviewIndex > 0) {
      setCurrent(previewUrlsKeys[currentPreviewIndex - 1])
    }
  }

  const onSwitchRight: React.MouseEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault()
    // Without this mask close will abnormal
    event.stopPropagation()
    if (currentPreviewIndex < previewGroupCount - 1) {
      setCurrent(previewUrlsKeys[currentPreviewIndex + 1])
    }
  }

  const wrapClassName = classnames({
    [`${prefixCls}-moving`]: isMoving,
  })
  const toolClassName = `${prefixCls}-operations-operation`
  const iconClassName = `${prefixCls}-operations-icon`
  const tools = [
    {
      icon: close,
      onClick: onClose,
      type: 'close',
    },
    {
      icon: zoomIn,
      onClick: onZoomIn,
      type: 'zoomIn',
    },
    {
      icon: zoomOut,
      onClick: onZoomOut,
      type: 'zoomOut',
      disabled: scale === 1,
    },
    {
      icon: rotateRight,
      onClick: onRotateRight,
      type: 'rotateRight',
    },
    {
      icon: rotateLeft,
      onClick: onRotateLeft,
      type: 'rotateLeft',
    },
  ]

  const onMouseUp: React.MouseEventHandler<HTMLBodyElement> = () => {
    if (visible && isMoving) {
      const width = imgRef.current!.offsetWidth * scale
      const height = imgRef.current!.offsetHeight * scale
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const { left, top } = imgRef.current!.getBoundingClientRect()
      const isRotate = rotate % 180 !== 0

      setMoving(false)

      const fixState = getFixScaleEleTransPosition(isRotate ? height : width, isRotate ? width : height, left, top)

      if (fixState) {
        setPosition({ ...fixState })
      }
    }
  }

  const onMouseDown: React.MouseEventHandler<HTMLDivElement> = (event) => {
    // Only allow main button
    if (event.button !== 0) return
    event.preventDefault()
    // Without this mask close will abnormal
    event.stopPropagation()
    originPositionRef.current.deltaX = event.pageX - position.x
    originPositionRef.current.deltaY = event.pageY - position.y
    originPositionRef.current.originX = position.x
    originPositionRef.current.originY = position.y
    setMoving(true)
  }

  const onMouseMove: React.MouseEventHandler<HTMLBodyElement> = (event) => {
    if (visible && isMoving) {
      setPosition({
        x: event.pageX - originPositionRef.current.deltaX,
        y: event.pageY - originPositionRef.current.deltaY,
      })
    }
  }

  React.useEffect(() => {
    let onTopMouseUpListener: any
    let onTopMouseMoveListener: any

    const onMouseUpListener = addEventListener(window, 'mouseup', onMouseUp, false)
    const onMouseMoveListener = addEventListener(window, 'mousemove', onMouseMove, false)

    try {
      // Resolve if in iframe lost event
      /* istanbul ignore next */
      if (window.top !== window.self) {
        onTopMouseUpListener = addEventListener(window.top, 'mouseup', onMouseUp, false)
        onTopMouseMoveListener = addEventListener(window.top, 'mousemove', onMouseMove, false)
      }
    } catch (error) {
      /* istanbul ignore next */
      warning(false, `[rc-image] ${error}`)
    }

    return () => {
      onMouseUpListener.remove()
      onMouseMoveListener.remove()

      /* istanbul ignore next */
      if (onTopMouseUpListener) onTopMouseUpListener.remove()
      /* istanbul ignore next */
      if (onTopMouseMoveListener) onTopMouseMoveListener.remove()
    }
  }, [visible, isMoving])

  return (
    <Dialog
      transitionName='zoom'
      maskTransitionName='fade'
      closable={false}
      keyboard
      prefixCls={prefixCls}
      onClose={onClose}
      afterClose={onAfterClose}
      visible={visible}
      wrapClassName={wrapClassName}
      {...restProps}
    >
      <ul className={`${prefixCls}-operations`}>
        {tools.map(({ icon, onClick, type, disabled }) => (
          <li
            className={classnames(toolClassName, {
              [`${prefixCls}-operations-operation-disabled`]: !!disabled,
            })}
            onClick={onClick}
            key={type}
          >
            {React.isValidElement(icon) ? React.cloneElement(icon, { className: iconClassName }) : icon}
          </li>
        ))}
      </ul>
      <div
        className={`${prefixCls}-img-wrapper`}
        style={{
          transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
        }}
      >
        <img
          onMouseDown={onMouseDown}
          ref={imgRef as any}
          className={`${prefixCls}-img`}
          src={combinationSrc}
          alt={alt}
          onLoad={(e) => {
            // 大图不要覆盖全屏
            setImageWidth((e.target as HTMLImageElement).naturalHeight > 2000 ? '60%' : 'auto')
          }}
          style={{
            width: imageWidth,
            transform: `scale3d(${scale}, ${scale}, 1) rotate(${rotate}deg)`,
          }}
        />
      </div>
      {showLeftOrRightSwitches && (
        <div
          className={classnames(`${prefixCls}-switch-left`, {
            [`${prefixCls}-switch-left-disabled`]: currentPreviewIndex === 0,
          })}
          onClick={onSwitchLeft}
        >
          {left}
        </div>
      )}
      {showLeftOrRightSwitches && (
        <div
          className={classnames(`${prefixCls}-switch-right`, {
            [`${prefixCls}-switch-right-disabled`]: currentPreviewIndex === previewGroupCount - 1,
          })}
          onClick={onSwitchRight}
        >
          {right}
        </div>
      )}
    </Dialog>
  )
}
